// OpenPGP key management for a room: your identity (generate/import), your
// public key to share, and the room's recipient public keys with fingerprints.
import { useEffect, useState } from "react";
import { pgp } from "../lib/crypto";

export function PgpPanel({ roomId }: { roomId: string }) {
  const [identity, setIdentity] = useState(pgp.getStoredIdentity());
  const [genName, setGenName] = useState("");
  const [genPass, setGenPass] = useState("");
  const [importArmored, setImportArmored] = useState("");
  const [recipient, setRecipient] = useState("");
  const [recipients, setRecipients] = useState<string[]>([]);
  const [fprints, setFprints] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function refreshRecipients() {
    setRecipients(pgp.getRoomRecipients(roomId));
    setFprints(await pgp.recipientFingerprints(roomId));
  }
  useEffect(() => {
    refreshRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  async function generate() {
    setError(null);
    try {
      if (genPass.length < 8) throw new Error("use a passphrase of 8+ chars to protect your key");
      setIdentity(await pgp.generateIdentity(genName, genPass));
      setGenPass("");
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function importKey() {
    setError(null);
    try {
      setIdentity(await pgp.importIdentity(importArmored.trim()));
      setImportArmored("");
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function addRecipient() {
    setError(null);
    try {
      await pgp.addRoomRecipient(roomId, recipient.trim());
      setRecipient("");
      await refreshRecipients();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="banner" style={{ margin: 0 }}>
      <h3 style={{ marginTop: 0 }}>OpenPGP keys</h3>
      {error && <p className="error">{error}</p>}

      {identity ? (
        <div style={{ marginBottom: 12 }}>
          <div className="muted">Your identity fingerprint:</div>
          <div className="mono" style={{ wordBreak: "break-all" }}>{identity.fingerprint}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button onClick={() => navigator.clipboard.writeText(identity.publicKeyArmored)}>
              Copy my public key
            </button>
            <button
              className="ghost"
              onClick={() => {
                if (confirm("Replace your identity? Old messages encrypted only to the old key become unreadable.")) {
                  pgp.clearStoredIdentity();
                  setIdentity(null);
                }
              }}
            >
              Reset identity
            </button>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <p className="muted" style={{ marginTop: 0 }}>
            You have no OpenPGP identity yet. Generate one (private key stays in your browser,
            passphrase-protected) or import an existing armored private key.
          </p>
          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <input placeholder="name / label" value={genName} onChange={(e) => setGenName(e.target.value)} />
            <input
              type="password"
              placeholder="key passphrase"
              value={genPass}
              onChange={(e) => setGenPass(e.target.value)}
            />
            <button className="primary" onClick={generate}>Generate</button>
          </div>
          <textarea
            rows={3}
            placeholder="…or paste an armored PRIVATE key to import"
            value={importArmored}
            onChange={(e) => setImportArmored(e.target.value)}
          />
          <button onClick={importKey} disabled={!importArmored.trim()}>Import private key</button>
        </div>
      )}

      <div>
        <div className="muted">Room recipients (their public keys):</div>
        {recipients.length === 0 && <p className="muted">None yet — paste a peer's public key below.</p>}
        <ul style={{ paddingLeft: 18 }}>
          {recipients.map((_, i) => (
            <li key={i} className="mono" style={{ fontSize: 12, wordBreak: "break-all" }}>
              {fprints[i]}{" "}
              <button
                className="linkbtn"
                onClick={async () => {
                  pgp.removeRoomRecipient(roomId, i);
                  await refreshRecipients();
                }}
              >
                remove
              </button>
            </li>
          ))}
        </ul>
        <textarea
          rows={3}
          placeholder="Paste a peer's armored PUBLIC key"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
        />
        <button onClick={addRecipient} disabled={!recipient.trim()}>Add recipient</button>
      </div>
    </div>
  );
}
